import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * Voice capabilities detected on the user's browser
 */
export interface VoiceCapabilities {
  webgpu: boolean;
  webSpeech: boolean;
  whisperLoaded: boolean;
  kokoroLoaded: boolean;
}

/**
 * User preferences for voice features
 */
export interface VoicePreferences {
  sttEngine: 'whisper' | 'webspeech' | 'auto';
  ttsEngine: 'kokoro' | 'webspeech' | 'auto';
  ttsVoice: string;
  ttsSpeed: number; // 0.5 to 2.0
  autoPlay: boolean;
  autoSubmitVoice: boolean;
  pushToTalkMode: boolean;
}

/**
 * Voice store state
 */
interface VoiceState {
  // Detected capabilities
  capabilities: VoiceCapabilities;

  // User preferences
  preferences: VoicePreferences;

  // Runtime state
  isListening: boolean;
  isSpeaking: boolean;
  currentUtterance: string | null;

  // Actions
  updateCapabilities: (caps: Partial<VoiceCapabilities>) => void;
  updatePreferences: (prefs: Partial<VoicePreferences>) => void;
  setIsListening: (listening: boolean) => void;
  setIsSpeaking: (speaking: boolean) => void;
  setCurrentUtterance: (text: string | null) => void;
  resetToDefaults: () => void;
}

/**
 * Default capabilities (before detection)
 */
const defaultCapabilities: VoiceCapabilities = {
  webgpu: false,
  webSpeech: typeof window !== 'undefined' && 'speechSynthesis' in window,
  whisperLoaded: false,
  kokoroLoaded: false,
};

/**
 * Default user preferences
 */
const defaultPreferences: VoicePreferences = {
  sttEngine: 'auto',
  ttsEngine: 'auto',
  ttsVoice: 'default',
  ttsSpeed: 1.0,
  autoPlay: false,
  autoSubmitVoice: false,
  pushToTalkMode: false,
};

/**
 * Voice store using Zustand with localStorage persistence
 * Manages voice capabilities, preferences, and runtime state
 */
export const useVoiceStore = create<VoiceState>()(
  persist(
    (set) => ({
      // Initial state
      capabilities: defaultCapabilities,
      preferences: defaultPreferences,
      isListening: false,
      isSpeaking: false,
      currentUtterance: null,

      // Update capabilities (detected on app load)
      updateCapabilities: (caps) =>
        set((state) => ({
          capabilities: { ...state.capabilities, ...caps },
        })),

      // Update user preferences
      updatePreferences: (prefs) =>
        set((state) => ({
          preferences: { ...state.preferences, ...prefs },
        })),

      // Set listening state
      setIsListening: (listening) => set({ isListening: listening }),

      // Set speaking state
      setIsSpeaking: (speaking) => set({ isSpeaking: speaking }),

      // Set current utterance being spoken
      setCurrentUtterance: (text) => set({ currentUtterance: text }),

      // Reset preferences to defaults
      resetToDefaults: () =>
        set({
          preferences: defaultPreferences,
        }),
    }),
    {
      name: 'neokanban-voice', // localStorage key
      storage: createJSONStorage(() => localStorage),
      // Only persist capabilities and preferences, not runtime state
      partialize: (state) => ({
        capabilities: state.capabilities,
        preferences: state.preferences,
      }),
    }
  )
);
